/**
 * Utility functions for checking username and email availability
 * Used for real-time validation in registration form
 */

import { supabase } from '@/clients/supabase';

export type AvailabilityStatus = 'checking' | 'available' | 'taken' | 'error';

/**
 * Check if username is available
 * @param username - Username to check (will be converted to lowercase)
 * @returns Promise with availability status
 */
export const checkUsernameAvailability = async (username: string): Promise<AvailabilityStatus> => {
  try {
    if (!username || username.length < 3) {
      return 'error'; // Too short, don't check
    }

    const lowercaseUsername = username.toLowerCase();
    
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .ilike('username', lowercaseUsername)
      .single();

    if (error) {
      // If no rows found (PGRST116), username is available
      if (error.code === 'PGRST116') {
        return 'available';
      }
      console.error('Username availability check error:', error);
      return 'error';
    }

    // If data exists, username is taken
    return data ? 'taken' : 'available';
  } catch (error) {
    console.error('Username availability check exception:', error);
    return 'error';
  }
};

/**
 * Normalize Gmail address by removing dots from username
 * Gmail ignores dots in the username part, so user@gmail.com = u.s.e.r@gmail.com
 * @param email - Email to normalize
 * @returns Normalized email address
 */
export const normalizeGmailAddress = (email: string): string => {
  const [username, domain] = email.toLowerCase().split('@');
  
  // Only normalize for Gmail domains (gmail.com and googlemail.com are the same)
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return `${username.replace(/\./g, '')}@gmail.com`; // Always use gmail.com
  }
  
  return email.toLowerCase();
};

/**
 * Check if email is available with Gmail dot trick detection
 * Uses database normalization function to find all Gmail variations
 * @param email - Email to check
 * @returns Promise with availability status
 */
export const checkEmailAvailability = async (email: string): Promise<AvailabilityStatus> => {
  try {
    if (!email || !email.includes('@')) {
      return 'error'; // Invalid email format
    }

    const inputEmail = email.toLowerCase();
    
    // Use database normalization function to check for any existing email
    // that would normalize to the same value as the input email
    const { data, error } = await supabase
      .rpc('check_email_availability', { 
        input_email: inputEmail 
      });

    if (error) {
      console.error('Email availability check error:', error);
      
      // Fallback to direct query if RPC fails
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('users')
        .select('id')
        .eq('email', inputEmail)
        .single();
        
      if (fallbackError && fallbackError.code === 'PGRST116') {
        return 'available';
      }
      
      return fallbackData ? 'taken' : 'available';
    }

    // RPC returns true if email is available, false if taken
    return data ? 'available' : 'taken';
  } catch (error) {
    console.error('Email availability check exception:', error);
    return 'error';
  }
};

/**
 * Debounce function to limit API calls
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};